import { OsContext, OsContextApi } from '@wpp-open/core'
import { connectToChild, Connection } from 'penpal'

import { trimUrl } from 'components/apps/fullscreen/microApp/utils'
import { FullscreenAppCustomProps } from 'types/common/singleSpa'

interface ChildMethods {
  onNavigate: (path: string) => void
  onOsContextUpdate: (data: { osContext: OsContext; accessToken: string }) => void
  onAccessTokenUpdate: (jwt: string) => void
}

export const setupIframeCommunication = (
  iframe: HTMLIFrameElement,
  currentBaseUrl: string,
  customProps: FullscreenAppCustomProps,
) => {
  const updateAccessToken = (jwt: string) => {
    connection.promise.then(child => {
      if (child.onAccessTokenUpdate) {
        child.onAccessTokenUpdate(jwt)
      }
    })
  }

  const methods = {
    parentSubscribe: () => {
      customProps.osContext.subscribe((data: OsContext) => {
        // NOTE: baseUrl should be '/' as inside of iframe router will be created from scratch and
        // will not follow parent window url as baseUrl
        const osContext = { ...data, baseUrlNativeApproach: data.baseUrl, baseUrl: '/' }
        connection.promise.then(child =>
          child.onOsContextUpdate({
            osContext: osContext as OsContext,
            accessToken: customProps.osApi.getAccessToken(),
          }),
        )
      })
    },
    onNavigate: (childPath: string) => {
      const currentPath = trimUrl(window.location.pathname)
      const targetPath = trimUrl(`${currentBaseUrl}${childPath}`)
      if (currentPath !== targetPath) {
        window.history.replaceState({}, '', `/${targetPath}`)
      }
    },

    // NOTE: Directly passing osApi methods into the iframe is not feasible because
    // PenPal automatically wraps all functions passed to the child in a promise.
    //
    // This would break the existing API. To maintain compatibility, we introduce
    // this method, which executes the osApi function at the parent level.
    //
    // From the iframe wrapper, the method is used as follows:
    //
    // openMenu: () => parent?.callParentOsApiMethod('openMenu', 'navigation')
    callParentOsApiMethod: <T extends keyof OsContextApi, M extends keyof OsContextApi[T]>(
      methodName: M,
      subfield: T,
      ...args: OsContextApi[T][M] extends (...p: infer P) => any ? P : never
    ) => {
      const apiSection = customProps.osApi[subfield]
      const method = apiSection[methodName]

      if (typeof method === 'function') {
        return method(...args)
      }

      console.warn(`Method ${String(methodName)} not found in ${String(subfield)}.`)
      return null
    },
    updateAccessToken,
  }

  const connection: Connection<ChildMethods> = connectToChild<ChildMethods>({
    iframe,
    methods,
  })

  return {
    cleanup: () => connection.destroy(),
    updateAccessToken,
  }
}
